import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Page, PageBody } from '@studio/legacy-components';

import {
  Shape as FlowShape,
  selectFlow,
  update as updateFlow,
} from 'next/entities/flows';
import {
  Shape as RuleShape,
  selectRule,
  update as updateRule,
} from 'next/entities/rules';
import { selectUser } from 'next/entities/user';
import { selectAccountUser } from 'next/entities/account-users';
import { replace as replacePage } from 'next/entities/page';
import { drop as dropContentStatus } from 'next/entities/content-status';

import TargetingDetails from 'next/components/TargetingDetails';
import UrlTargeting from 'next/components/UrlTargeting';
import Audience from 'next/components/Audience';

import useTitle from 'next/hooks/use-title';
import { getFlowTargetingSections } from './get-flow-targeting-sections';
import { Loading } from './styled';

export const SettingsPage = ({
  loaded,
  flow,
  rule,
  onLoad,
  onUnload,
  onFlowChange,
  onRuleChange,
}) => {
  useTitle(`${flow?.name} | Settings | Appcues`);
  useEffect(() => {
    onLoad();
    return () => onUnload();
  }, [onLoad, onUnload]);

  if (!loaded) {
    return <Loading />;
  }

  const sections = getFlowTargetingSections(rule.conditions);

  const handleSectionChange = section => newConditions =>
    void onRuleChange({
      conditions: {
        and: Object.values({
          ...sections,
          [section]: newConditions,
        }).filter(branch => branch && (branch.and || branch.or).length > 0),
      },
    });

  return (
    <Page>
      <PageBody>
        <TargetingDetails
          name={flow.name}
          previewUrl={flow.previewUrl}
          onChange={onFlowChange}
        />
        <UrlTargeting
          urlConditions={sections.url}
          domainConditions={sections.domains}
          onUrlConditionChange={handleSectionChange('url')}
          onDomainConditionChange={handleSectionChange('domains')}
        />
        <Audience
          experienceName="Flow"
          conditions={sections.audience}
          onChange={handleSectionChange('audience')}
        />
      </PageBody>
    </Page>
  );
};

SettingsPage.propTypes = {
  loaded: PropTypes.bool,
  flow: FlowShape,
  rule: RuleShape,
  onLoad: PropTypes.func,
  onUnload: PropTypes.func,
  onFlowChange: PropTypes.func,
  onRuleChange: PropTypes.func,
};

const mapStateToProps = (
  state,
  {
    match: {
      params: { flowId },
    },
  }
) => {
  const flow = selectFlow(state, flowId);
  const rule = selectRule(state, flowId);
  const { id: userId } = selectUser(state);
  const accountUser = selectAccountUser(state, userId);

  return {
    id: flowId,
    loaded: Boolean(flow && rule && accountUser),
    flow,
    rule,
  };
};

const mapDispatchToProps = (
  dispatch,
  {
    match: {
      path,
      params: { flowId: id },
    },
  }
) => ({
  onLoad: () => dispatch(replacePage({ path, id })),
  onUnload: () => dispatch(dropContentStatus(id)),
  onFlowChange: delta => dispatch(updateFlow(id, delta)),
  onRuleChange: delta => dispatch(updateRule(id, delta)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsPage);
